import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';

const FichaTecnicaDetalhes = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recalculando, setRecalculando] = useState(false);

  useEffect(() => {
    // ✅ PROTEÇÃO: Verificar se ID é válido
    if (!id || id === 'nova' || id === 'editar' || isNaN(id)) {
      console.warn('ID inválido detectado:', id);
      navigate('/fichas-tecnicas');
      return;
    }

    carregarFicha();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const carregarFicha = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/fichas/api/fichas-tecnicas/${id}`);
      
      if (response.data.success) {
        setFicha(response.data.ficha);
      } else {
        setError('Ficha técnica não encontrada');
      }
    } catch (err) {
      console.error('Erro ao carregar ficha:', err);
      setError('Erro ao carregar ficha técnica');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalcular = async () => {
    try {
      setRecalculando(true);
      const response = await api.post(`/fichas/api/fichas-tecnicas/${id}/recalcular`);
      
      if (response.data.success) {
        alert('Custos recalculados com sucesso!');
        carregarFicha();
      }
    } catch (err) {
      alert('Erro ao recalcular custos');
      console.error(err);
    } finally {
      setRecalculando(false);
    }
  };

  const handleDeletar = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir a ficha técnica "${ficha.nome}"?`)) {
      return;
    }

    try {
      const response = await api.delete(`/fichas/api/fichas-tecnicas/${id}`);
      
      if (response.data.success) {
        alert('Ficha técnica excluída com sucesso!');
        navigate('/fichas-tecnicas');
      }
    } catch (err) {
      alert('Erro ao excluir ficha técnica');
      console.error(err);
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor) return 'R$ 0,00';
    return `R$ ${parseFloat(valor).toFixed(2).replace('.', ',')}`;
  };

  const formatarPercentual = (valor) => {
    if (!valor) return '0%';
    return `${parseFloat(valor).toFixed(1)}%`;
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ficha) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          {error || 'Ficha técnica não encontrada'}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/fichas-tecnicas')}>
          ← Voltar
        </button>
      </div>
    );
  }

  // Separar itens por tipo
  const insumos = ficha.itens?.filter(i => i.tipo === 'insumo') || [];
  const embalagens = ficha.itens?.filter(i => i.tipo === 'embalagem') || [];
  const isProdutoFinal = ficha.tipo_ficha === 'produto_final';


// Template de impressão para produção
const gerarTemplateImpressao = () => {
  const multiplicadores = [1, 2, 3, 4, 5, 6, 0.5];
  
  // Criar nova janela
  const printWindow = window.open('', '_blank');
  
  // Combinar todos os ingredientes em uma única lista
  const todosIngredientes = [
    ...ficha.itens.filter(i => i.tipo === 'insumo'),
    ...(ficha.sub_receitas || []).map(sub => ({
      nome: `🍰 ${sub.nome}`,
      quantidade: sub.quantidade,
      unidade_medida: sub.unidade_medida || 'porção',
      tipo: 'sub_receita'
    }))
  ];
  
  const embalagens = ficha.itens.filter(i => i.tipo === 'embalagem');
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${ficha.nome} - Template</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        h1 {
          text-align: center;
          color: #333;
          border-bottom: 3px solid #5B4740;
          padding-bottom: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: center;
        }
        th {
          background-color: #f8b5b5;
          font-weight: bold;
        }
        td:first-child {
          text-align: left;
          font-weight: bold;
        }
        .sub-receita {
          background-color: #fff9e6;
        }
        .modo-preparo {
          margin: 20px 0;
          padding: 15px;
          background-color: #f9f9f9;
          border-left: 4px solid #5B4740;
        }
        .modo-preparo h3 {
          margin-top: 0;
          color: #5B4740;
        }
        .rendimento {
          margin: 20px 0;
          padding: 10px;
          background-color: #e8f4f8;
          border-radius: 5px;
          text-align: center;
          font-size: 18px;
          font-weight: bold;
        }
        @media print {
          body { padding: 10px; }
          table { font-size: 12px; }
        }
      </style>
    </head>
    <body>
      <h1>${ficha.nome}</h1>
      
      <h2 style="text-align: center; color: #666;">Quantidade de Receitas</h2>
      
      <table>
        <thead>
          <tr>
            <th>Ingredientes</th>
            ${multiplicadores.map(m => `<th>${m === 0.5 ? '1/2x' : m + 'x'}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${todosIngredientes.map(item => `
            <tr ${item.tipo === 'sub_receita' ? 'class="sub-receita"' : ''}>
              <td>${item.nome}</td>
              ${multiplicadores.map(m => {
                const qtd = (item.quantidade * m);
                const qtdFormatada = qtd % 1 === 0 ? qtd : qtd.toFixed(2).replace('.', ',');
                return `<td>${qtdFormatada} ${item.unidade_medida}</td>`;
              }).join('')}
            </tr>
          `).join('')}
          
          ${embalagens.length > 0 ? `
            <tr style="background-color: #f0f0f0;">
              <td colspan="${multiplicadores.length + 1}" style="text-align: center; font-weight: bold; padding: 5px;">
                📦 EMBALAGENS
              </td>
            </tr>
            ${embalagens.map(item => `
              <tr>
                <td>${item.nome}</td>
                ${multiplicadores.map(m => {
                  const qtd = Math.ceil(item.quantidade * m);
                  return `<td>${qtd}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          ` : ''}
        </tbody>
      </table>
      
      ${ficha.modo_preparo ? `
        <div class="modo-preparo">
          <h3>👨‍🍳 Modo de Preparo</h3>
          <p style="white-space: pre-wrap; line-height: 1.6;">${ficha.modo_preparo}</p>
        </div>
      ` : ''}
      
      <div class="rendimento">
        <strong>Rendimento:</strong> ${ficha.rendimento} ${ficha.unidade_rendimento}${ficha.peso_total ? ` (${ficha.peso_total}g)` : ''}
      </div>

      <div style="text-align:center; margin: 24px 0 8px;">
        <button onclick="window.print()" style="background:#5B4740;color:#fff;border:none;padding:10px 28px;border-radius:8px;font-size:15px;cursor:pointer;font-family:Arial,sans-serif;">
          🖨️ Imprimir
        </button>
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(html);
  printWindow.document.close();
};

return (
  <div className="container mt-4">
    <style>{`
      .modo-preparo-content {
        counter-reset: step-counter;
      }
      
      .modo-preparo-content p {
        margin-bottom: 0.8em;
      }
      
      @media print {
        .modo-preparo-content {
          font-size: 12px;
        }
      }
    `}</style>
    
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            {isProdutoFinal ? '🎂' : '🧈'} {ficha.nome}
          </h2>
          <div className="d-flex gap-2 align-items-center mt-2">
            <span className={`badge ${isProdutoFinal ? 'bg-success' : 'bg-warning text-dark'} fs-6`}>
              {isProdutoFinal ? 'Produto Final' : 'Preparação Base'}
            </span>
            {ficha.categoria && (
              <span className="badge bg-secondary fs-6">{ficha.categoria}</span>
            )}
          </div>
        </div>
        <div className="btn-group">
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/fichas-tecnicas')}
          >
            ← Voltar
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/fichas-tecnicas/${id}/editar`)}
          >
            ✏️ Editar
          </button>
          <button 
            className="btn btn-info"
            onClick={handleRecalcular}
            disabled={recalculando}
          >
            {recalculando ? '⏳' : '🔄'} Recalcular
          </button>
          <button 
            className="btn btn-danger"
            onClick={handleDeletar}
          >
            🗑️ Excluir
          </button>
        </div>
      </div>

      {/* Informações Básicas */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">📝 Informações</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <p><strong>Código:</strong> {ficha.codigo || '-'}</p>
              <p><strong>Rendimento:</strong> {ficha.rendimento} {ficha.unidade_rendimento}</p>
              {ficha.peso_total && (
                <p><strong>Peso Total:</strong> {ficha.peso_total}g</p>
              )}
              <p><strong>Tempo de Preparo:</strong> {ficha.tempo_preparo_minutos ? `${ficha.tempo_preparo_minutos} minutos` : '-'}</p>
            </div>
            <div className="col-md-6">
              <p><strong>Criado em:</strong> {formatarData(ficha.data_criacao)}</p>
              <p><strong>Atualizado em:</strong> {formatarData(ficha.data_atualizacao)}</p>
              <p><strong>Status:</strong> <span className={`badge ${ficha.ativo ? 'bg-success' : 'bg-danger'}`}>{ficha.ativo ? 'Ativo' : 'Inativo'}</span></p>
            </div>
          </div>
          {ficha.descricao && (
            <div className="mt-3">
              <strong>Descrição:</strong>
              <p className="mb-0">{ficha.descricao}</p>
            </div>
          )}
        </div>
      </div>

      {/* Insumos */}
      {insumos.length > 0 && (
        <div className="card mb-4">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">🥄 Insumos ({insumos.length})</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="text-center">Quantidade</th>
                    <th className="text-center">Unidade</th>
                    <th className="text-end">Custo Unitário</th>
                    <th className="text-end">Custo Total</th>
                    <th>Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {insumos.map((item, index) => (
                    <tr key={index}>
                      <td><strong>{item.nome}</strong></td>
                      <td className="text-center">{item.quantidade}</td>
                      <td className="text-center">{item.unidade_medida}</td>
                      <td className="text-end">{formatarMoeda(item.custo_unitario)}</td>
                      <td className="text-end">
                        <strong>{formatarMoeda(item.custo_total)}</strong>
                      </td>
                      <td>
                        {item.observacoes && (
                          <small className="text-muted">{item.observacoes}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light fw-bold">
                    <td colSpan="4" className="text-end">Subtotal Insumos:</td>
                    <td className="text-end text-primary">
                      {formatarMoeda(insumos.reduce((sum, i) => sum + (i.custo_total || 0), 0))}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Receitas - MOSTRAR SEMPRE QUE EXISTIR */}
      {ficha.sub_receitas && ficha.sub_receitas.length > 0 && (
        <div className="card mb-4 border-warning">
          <div className="card-header bg-warning bg-opacity-10">
            <h5 className="mb-0">🍰 Preparações Base / Sub-Receitas ({ficha.sub_receitas.length})</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Preparação</th>
                    <th className="text-center">Quantidade</th>
                    <th className="text-center">Unidade</th>
                    <th className="text-end">Custo Unit.</th>
                    <th className="text-end">Custo Total</th>
                  </tr>
                </thead>
                <tbody>
                  {ficha.sub_receitas.map((sub, index) => (
                    <tr key={index}>
                      <td><strong>{sub.nome}</strong></td>
                      <td className="text-center">{sub.quantidade}</td>
                      <td className="text-center">{sub.unidade_medida || sub.unidade_rendimento}</td>
                      <td className="text-end">{formatarMoeda(sub.custo_unitario)}</td>
                      <td className="text-end"><strong>{formatarMoeda(sub.custo_total)}</strong></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-warning fw-bold">
                    <td colSpan="4" className="text-end">Subtotal Preparações:</td>
                    <td className="text-end">
                      {formatarMoeda(ficha.sub_receitas.reduce((sum, s) => sum + (s.custo_total || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Embalagens */}
      {embalagens.length > 0 && (
        <div className="card mb-4">
          <div className="card-header bg-secondary text-white">
            <h5 className="mb-0">📦 Embalagens ({embalagens.length})</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="text-center">Quantidade</th>
                    <th className="text-end">Custo Unitário</th>
                    <th className="text-end">Custo Total</th>
                    <th>Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {embalagens.map((item, index) => (
                    <tr key={index}>
                      <td><strong>{item.nome}</strong></td>
                      <td className="text-center">{item.quantidade}</td>
                      <td className="text-end">{formatarMoeda(item.custo_unitario)}</td>
                      <td className="text-end">
                        <strong>{formatarMoeda(item.custo_total)}</strong>
                      </td>
                      <td>
                        {item.observacoes && (
                          <small className="text-muted">{item.observacoes}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light fw-bold">
                    <td colSpan="3" className="text-end">Subtotal Embalagens:</td>
                    <td className="text-end text-secondary">
                      {formatarMoeda(embalagens.reduce((sum, i) => sum + (i.custo_total || 0), 0))}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Resumo de Custos */}
      <div className="card mb-4 border-primary">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">💰 Custos</h5>
        </div>
        <div className="card-body">
          <div className="row text-center g-3">
            <div className="col-md-6">
              <div className="p-3 bg-light rounded">
                <small className="text-muted d-block">Custo Total</small>
                <h4 className="text-primary mb-0">{formatarMoeda(ficha.custo_total)}</h4>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-3 bg-light rounded">
                <small className="text-muted d-block">Custo por {ficha.unidade_rendimento}</small>
                <h4 className="text-info mb-0">{formatarMoeda(ficha.custo_unitario)}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Precificação - SOMENTE PARA PRODUTO FINAL */}
      {isProdutoFinal && (
        <div className="card mb-4 border-success">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">💰 Precificação</h5>
          </div>
          <div className="card-body">
            <div className="row text-center g-3">
              <div className="col">
                <div className="p-3 bg-light rounded">
                  <small className="text-muted d-block">Preço Sugerido</small>
                  <h4 className="text-success mb-0">{formatarMoeda(ficha.preco_venda_sugerido)}</h4>
                </div>
              </div>
              <div className="col">
                <div className="p-3 bg-light rounded">
                  <small className="text-muted d-block">Lucro por Unidade</small>
                  <h4 className="text-warning mb-0">
                    {formatarMoeda((ficha.preco_venda_sugerido || 0) - (ficha.custo_unitario || 0))}
                  </h4>
                </div>
              </div>
              <div className="col">
                <div className="p-3 bg-light rounded">
                  <small className="text-muted d-block">Markup</small>
                  <h4 className="text-primary mb-0">
                    {ficha.custo_unitario > 0
                      ? `${(ficha.preco_venda_sugerido / ficha.custo_unitario).toFixed(2)}x`
                      : '-'}
                  </h4>
                </div>
              </div>
              <div className="col">
                <div className="p-3 bg-light rounded">
                  <small className="text-muted d-block">CMV</small>
                  <h4 className={`mb-0 ${ficha.cmv_percentual > 35 ? 'text-danger' : 'text-success'}`}>
                    {formatarPercentual(ficha.cmv_percentual)}
                  </h4>
                  <small className="text-muted">Ideal: 25-35%</small>
                </div>
              </div>
              <div className="col">
                <div className="p-3 bg-light rounded">
                  <small className="text-muted d-block">Margem de Lucro</small>
                  <h4 className="text-info mb-0">{formatarPercentual(ficha.margem_lucro_percentual)}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modo de Preparo */}
      {ficha.modo_preparo && (
        <div className="card mb-4 border-info">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">👨‍🍳 Modo de Preparo</h5>
          </div>
          <div className="card-body">
            <div 
              className="modo-preparo-content"
              style={{ 
                whiteSpace: 'pre-wrap',
                lineHeight: '1.8',
                fontSize: '15px'
              }}
            >
              {ficha.modo_preparo}
            </div>
          </div>
        </div>
      )}

      {/* Botões de impressão */}
      <div className="text-center mb-4 d-flex gap-2 justify-content-center">
        <button 
          className="btn btn-outline-primary"
          onClick={() => window.print()}
        >
          🖨️ Imprimir Ficha Técnica
        </button>
        
        <button 
          className="btn btn-outline-success"
          onClick={gerarTemplateImpressao}
        >
          📋 Template de Produção
        </button>
      </div>
    </div>
  );
};

export default FichaTecnicaDetalhes;